import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router-dom';
import { uploadBusinessCustomerDeleteOrRestoreList } from '../../../api/admin/customers/customers';
// Components
import CentsLoader from '../../common/centsLoader/CentsLoader';

const UploadBusinessCustomerDeleteOrRestore = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);

  const history = useHistory();

  /**
   * Handle file drag and drop
   */
  const onDrop = useCallback((acceptedFiles) => {
    const [file] = acceptedFiles;
    if (!file) return;

    const reader = new FileReader();

    reader.onabort = () => console.log('file reading was aborted');
    reader.onerror = () => console.log('file reading has failed');
    reader.onload = () => {
      setSelectedFile(reader.result);
      setSelectedFileName(file.path);
    };

    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: '.csv,text/csv',
  });

  /**
   * Upload the CSV of businessCustomerId/deleted rows
   */
  const saveDeleteOrRestoreList = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const { data } = await uploadBusinessCustomerDeleteOrRestoreList({
        fileToUpload: selectedFile,
      });

      setUploadSummary({
        queuedCount: data.queuedCount,
        invalidRowCount: data.invalidRowCount,
      });
    } catch (error) {
      const responseError = error.response && error.response.data && error.response.data.error;
      setErrorMessage(responseError || 'Unable to upload this file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset the form to upload another file
   */
  const uploadAnotherFile = () => {
    setSelectedFile(null);
    setSelectedFileName(null);
    setUploadSummary(null);
    setErrorMessage(null);
  };

  /**
   * Render the completion summary after a successful upload
   */
  const renderUploadSummary = () => (
    <>
      <h1>Upload Queued</h1>
      <p>
        {uploadSummary.queuedCount} row{uploadSummary.queuedCount === 1 ? '' : 's'} queued for
        processing.
      </p>
      {uploadSummary.invalidRowCount > 0 && (
        <p>
          {uploadSummary.invalidRowCount} invalid row
          {uploadSummary.invalidRowCount === 1 ? ' was' : 's were'} skipped.
        </p>
      )}
      <p>
        Rows are processed asynchronously in the background — it may take a few minutes for all
        changes to take effect.
      </p>
      <div className="submit-button-container">
        <button className="submit-button active" onClick={uploadAnotherFile}>
          Upload Another File
        </button>
        <button className="submit-button active" onClick={() => history.push('/customers')}>
          Back to Customers
        </button>
      </div>
    </>
  );

  /**
   * Render the UI to upload the delete/restore CSV
   */
  const renderUploadFileSelection = () => (
    <>
      <h1>Select Your File</h1>
      <p>
        Please note: the file must be a CSV and must include the following headers EXACTLY as they
        appear:
      </p>
      <ol>
        <li>
          businessCustomerId (database id from the <code>businessCustomers</code> table)
        </li>
        <li>deleted (true or false)</li>
      </ol>
      <p>
        Set <code>deleted</code> to <code>true</code> to soft-delete a business customer, or{' '}
        <code>false</code> to restore one previously soft-deleted this way. This also archives or
        re-syncs the customer with any connected external integrations (e.g. PocketFlows).
      </p>
      <div className="upload-customers-form-row">
        <p className="label">File</p>
        <div className="open-picker-container">
          {!selectedFileName && (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              {isDragActive ? (
                <p>Drop the files here ...</p>
              ) : (
                <p>Drag and drop or click to select files</p>
              )}
            </div>
          )}
          {selectedFileName && <p style={{ textAlign: 'center' }}>{selectedFileName}</p>}
        </div>
        {fileRejections.length > 0 && (
          <div className="error-container">
            {fileRejections.map(({ file, errors }) => (
              <p className="error-text" key={file.path}>
                {file.path}: {errors.map((e) => e.message).join(', ')}
              </p>
            ))}
          </div>
        )}
      </div>
      <div className="submit-button-container">
        <button
          className={'submit-button ' + (!selectedFile ? 'disabled' : 'active')}
          onClick={saveDeleteOrRestoreList}
          disabled={!selectedFile}
        >
          Submit
        </button>
      </div>
      {errorMessage && (
        <div className="error-container">
          <p className="error-text">{errorMessage}</p>
        </div>
      )}
    </>
  );

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <div className="upload-customer-container">
          <div className="upload-customers-header">
            <h1>Bulk Delete/Restore Business Customers</h1>
          </div>
          <div className="upload-customers-form-container">
            <div className="upload-customers-form">
              {uploadSummary ? renderUploadSummary() : renderUploadFileSelection()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

UploadBusinessCustomerDeleteOrRestore.displayName = 'UploadBusinessCustomerDeleteOrRestore';

export default UploadBusinessCustomerDeleteOrRestore;
